/* eslint-disable jsx-a11y/anchor-is-valid */
import { Form, Input, Modal, Popconfirm, Space, Spin, Table, Tooltip, Typography } from "antd";
import React, { useState } from "react";
import { HiOutlineClock, HiOutlineTrash, HiPencilSquare } from "react-icons/hi2";
import { useQueryClient } from "@tanstack/react-query";
import { DateHelper } from "../../../../../helper";
import { ApprovalStatus } from "../../../../../components";
import { CreateNotification } from "../../../../../components/notifier";
import { DateFormat, EmailType, RegExp } from "../../../../../constants";
import { useBusinessTicketsRequests } from "../../../../procedure/company-office/business-tickets-v2/hooks";
import { BusinessTicketRequestService } from "../../../../../services/company-office/BusinessTicketService";
import { useColumn } from "../../Employees/hooks/use-column";
import { EditableCell } from "../../Components/editabel-column";
import { useUser } from "../../../../../hook";
import { useSendMail } from "../../Employees/hooks/use-send-mail";
import { ProcessHistoryModal } from "../../Components/view-history";
import { Routes } from "../../../../../AppRoutes";
import { useCheckPermission } from "../../Permission/hooks/use-check-permission";
import { configPermission } from "../../../../../constants/configPermission";

const ConfigBusinessTicketRequest = () => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { email, name } = useUser();
  const [requestId, setRequestId] = useState("");
  const { sendMailUpdateApproval, sendMailDeleteRequest } = useSendMail();
  const { businessTicketsRequests, isLoading } = useBusinessTicketsRequests();
  const { getColumnSearchProps, cancel, editingKey, getDataSource, isEditing, isReject, mergedColumns, setEditingKey } =
    useColumn();

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const isUpdate = useCheckPermission(configPermission.UPDATE);
  const isDelete = useCheckPermission(configPermission.DELETE);

  const dataSource = getDataSource(businessTicketsRequests);

  const showDeleteConfirm = (record: any) => {
    const { TextArea } = Input;
    let deleteMessage = "";
    Modal.confirm({
      title: "Thông báo",
      content: (
        <TextArea
          rows={4}
          placeholder="Vui lòng nhập lý do xoá"
          onChange={(e: any) => (deleteMessage = e.target.value)}
        />
      ),
      okText: "Xác nhận",
      okType: "danger",
      okButtonProps: { type: "primary" },
      cancelText: "Huỷ",
      onOk() {
        onRemove(record, deleteMessage);
      },
    });
  };

  const onRemove = async (record: any, deleteMessage: string) => {
    try {
      await BusinessTicketRequestService.deleteBusinessTicketRequest(record.id);
      CreateNotification({
        title: "Thành công",
        body: "Xoá phiếu đã chọn thành công",
        icon: "/icons/success.png",
      });
      queryClient.invalidateQueries({ queryKey: ["business-tickets-requests"] });
      const mailParams = {
        mailTo: { name: record.createdName, email: record.createdMail },
        eMailType: EmailType.RDE,
        requestName: `Cấp/Hoàn vé công tác (số phiếu: ${record.id})`,
        deletor: { name: name, email: email },
        deleteMessage: deleteMessage,
      };

      await sendMailDeleteRequest(mailParams);
    } catch (error) {}
  };

  const edit = (record: Partial<any> & { key: React.Key }) => {
    const id = record.id;
    setEditingKey(id);
    form.setFieldsValue({ id, ...record });
  };

  const save = async (record: any) => {
    try {
      const row = (await form.validateFields()) as any;
      const res = await BusinessTicketRequestService.updateBusinessTicketRequest(editingKey, row);
      if (res) {
        CreateNotification({
          title: "Thành công",
          body: "Cập nhật thông tin quy trình thành công",
          icon: "/icons/success.png",
        });
        sendMailUpdateApproval(
          record,
          row,
          { approval: EmailType.BTRA, change: EmailType.BTRAC },
          Routes.procedureCollaborateTicketDetailPage.path.replace(":id", record.id),
        );
      } else
        CreateNotification({
          title: "Không thể cập nhật",
          body: "Không thể cập nhật quy trình, vui lòng thử lại",
          icon: "/icons/warning.png",
        });
      setEditingKey("");
      queryClient.invalidateQueries({ queryKey: ["business-tickets-requests"] });
    } catch (errInfo) {
      console.error("Validate Failed:", errInfo);
    }
  };

  const handleOpenHistoryModal = async (record: any) => {
    const res = await BusinessTicketRequestService.getHistory(record.id);
    setHistoryData(res);
    setRequestId(record.id);
    setIsOpenModal(true);
  };

  const onCloseModal = () => {
    setHistoryData([]);
    setIsOpenModal(false);
  };

  const columns = [
    {
      key: "action",
      dataIndex: "action",
      width: 30,
      align: "left" as const,
      render: (_: string, record: any) => {
        const editable = isEditing(record);
        return (
          <Space>
            {editable ? (
              <p style={{ width: "80px" }}>
                <Typography.Link onClick={() => save(record)} style={{ marginRight: 8 }}>
                  Lưu
                </Typography.Link>
                <Popconfirm title="Anh/Chị có muốn huỷ?" okText="Đồng ý" cancelText="Không" onConfirm={cancel}>
                  <a>Huỷ</a>
                </Popconfirm>
              </p>
            ) : (
              <>
                {isUpdate && (
                  <Typography.Link disabled={editingKey !== "" || isReject(record)} onClick={() => edit(record)}>
                    <Tooltip title="Chỉnh sửa">
                      <HiPencilSquare
                        size={20}
                        className="cursor-pointer"
                        color={editingKey || isReject(record) ? "#CEE3F3" : "#0ea5e9"}
                      />
                    </Tooltip>
                  </Typography.Link>
                )}
                <Typography.Link disabled={editingKey !== ""} onClick={() => handleOpenHistoryModal(record)}>
                  <Tooltip title="Lịch sử chỉnh sửa">
                    <HiOutlineClock size={20} className="cursor-pointer" color={editingKey ? "#CEE3F3" : "#0ea5e9"} />
                  </Tooltip>
                </Typography.Link>
                {isDelete && (
                  <Typography.Link disabled={editingKey !== ""} onClick={() => showDeleteConfirm(record)}>
                    <Tooltip title="Xoá">
                      <HiOutlineTrash
                        size={20}
                        className="cursor-pointer"
                        color={!editingKey ? "#dc2626" : "#CEE3F3"}
                      />
                    </Tooltip>
                  </Typography.Link>
                )}
              </>
            )}
          </Space>
        );
      },
    },
    {
      title: "Số phiếu",
      dataIndex: "id",
      editable: false,
      align: "center" as const,
      ...getColumnSearchProps("id", "Vui lòng nhập số phiếu"),
    },
    {
      title: "Người tạo",
      dataIndex: "createdName",
      editable: false,
      width: 200,
      ...getColumnSearchProps("createdName", "Vui lòng nhập người tạo"),
    },
    {
      title: "Tiêu đề",
      dataIndex: "Title",
      editable: false,
      width: 200,
    },
    {
      title: "Mô tả",
      dataIndex: "eDescription",
      editable: false,
      width: 400,
    },
    {
      title: "Tình trạng",
      dataIndex: "eRequestStatus",
      editable: false,
      render: (text: string) => <ApprovalStatus status={text} />,
    },
    {
      title: "Thủ trưởng CQ/ĐV",
      dataIndex: "eApprovedBy",
      editable: false,
      render: (_: any, record: any) =>
        !record.eApprovedStatus && isEditing(record) ? (
          <Form.Item
            name={"eApprovedBy"}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Vui lòng nhập thủ trưởng CQ/ĐV!`,
              },
              {
                message: "Vui lòng nhập đúng định dạng: mail@viags.vn|Họ và tên",
                pattern: RegExp.approver,
              },
            ]}
          >
            <Input />
          </Form.Item>
        ) : (
          record.eApprovedBy
        ),
    },
    {
      title: "Trạng thái Thủ trưởng CQ/ĐV",
      dataIndex: "eApprovedStatus",
      editable: false,
      render: (text: string) => <ApprovalStatus status={text} />,
    },
    {
      title: "Ngày Thủ trưởng CQ/ĐV phê duyệt",
      dataIndex: "eApprovedDate",
      editable: false,
      render: (text: string) => DateHelper.formatDateToString(text, { format: DateFormat.DD_MM_YYYY_HH_MM }),
    },
    {
      title: "Bộ phận hành chính VPCT",
      dataIndex: "eApprovedByITLead",
      editable: false,
      render: (_: any, record: any) =>
        !record.eApprovedByITLeadStatus && isEditing(record) ? (
          <Form.Item
            name={"eApprovedByITLead"}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Vui lòng nhập Bộ phận hành chính VPCT!`,
              },
              {
                message: "Vui lòng nhập đúng định dạng: mail@viags.vn|Họ và tên",
                pattern: RegExp.approver,
              },
            ]}
          >
            <Input />
          </Form.Item>
        ) : (
          record.eApprovedByITLead
        ),
    },
    {
      title: "Trạng thái bộ phận hành chính VPCT",
      dataIndex: "eApprovedByITLeadStatus",
      editable: false,
      render: (text: string) => <ApprovalStatus status={text} />,
    },
    {
      title: "Ngày bộ phận hành chính VPCT phê duyệt",
      dataIndex: "eApprovedByITLeadDate",
      editable: false,
      render: (text: string) => DateHelper.formatDateToString(text, { format: DateFormat.DD_MM_YYYY_HH_MM }),
    },
    {
      title: "Cán Bộ VPCT",
      dataIndex: "eApprovedByITHead",
      editable: false,
      render: (_: any, record: any) =>
        !record.eApprovedByITHeadStatus && isEditing(record) ? (
          <Form.Item
            name={"eApprovedByITHead"}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Vui lòng nhập Cán Bộ VPCT!`,
              },
              {
                message: "Vui lòng nhập đúng định dạng: mail@viags.vn|Họ và tên",
                pattern: RegExp.approver,
              },
            ]}
          >
            <Input />
          </Form.Item>
        ) : (
          record.eApprovedByITHead
        ),
    },
    {
      title: "Trạng thái Cán Bộ VPCT",
      dataIndex: "eApprovedByITHeadStatus",
      editable: false,
      render: (text: string) => <ApprovalStatus status={text} />,
    },
    {
      title: "Ngày Cán Bộ VPCT phê duyệt",
      dataIndex: "eApprovedByITHeadDate",
      editable: false,
      render: (text: string) => DateHelper.formatDateToString(text, { format: DateFormat.DD_MM_YYYY_HH_MM }),
    },
    {
      title: "Thực hiện cấp vé",
      dataIndex: "eAssignee",
      editable: false,
      render: (_: any, record: any) =>
        !record.eAssigneeStatus && isEditing(record) ? (
          <Form.Item
            name={"eAssignee"}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Vui lòng nhập cán bộ Thực hiện cấp vé !`,
              },
              {
                message: "Vui lòng nhập đúng định dạng: mail@viags.vn|Họ và tên",
                pattern: RegExp.approver,
              },
            ]}
          >
            <Input />
          </Form.Item>
        ) : (
          record.eAssignee
        ),
    },
    {
      title: "Trạng thái thực hiện",
      dataIndex: "eAssigneeStatus",
      editable: false,
      render: (text: string) => <ApprovalStatus status={text} />,
    },
    {
      title: "Ngày thực hiện",
      dataIndex: "eAssigneeStatusDate",
      editable: false,
      render: (text: string) => DateHelper.formatDateToString(text, { format: DateFormat.DD_MM_YYYY_HH_MM }),
    },
  ];

  return (
    <div>
      <Spin spinning={isLoading}>
        <Form form={form} component={false}>
          <Form.Item>
            <Table
              bordered
              dataSource={dataSource}
              columns={mergedColumns(columns)}
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              rowClassName="editable-row"
              style={{ overflowX: "auto" }}
              scroll={{ x: "3100px" }}
            />
          </Form.Item>
        </Form>
      </Spin>
      <ProcessHistoryModal id={requestId} isOpen={isOpenModal} onClose={onCloseModal} historyData={historyData} />
    </div>
  );
};

export default ConfigBusinessTicketRequest;
